import { useEnsureModalInteraction } from '@milanote/roma/utils/ensure-modal-interaction';

// Best effort list of selectors used by popular password managers to identify
// their UI elements. This list may not be exhaustive or up-to-date.
export const PASSWORD_MANAGER_SELECTORS = [
    // ---------------------------------------------------------------------------
    // 1Password (injects custom elements; tag name contains "1password")
    // Evidence: <com-1password-button> used for detection in academic work.  https://www.usenix.org/system/files/usenixsecurity25-anliker.pdf
    // ---------------------------------------------------------------------------
    'com-1password-button',
    'com-1password-menu',
    // Common defensive patterns seen in codebases / “just match 1password tags”
    "[id^='com-1password-']",
    "[id^='onepassword-']",
    "[id*='1password']",
    "[class*='1password']",

    // ---------------------------------------------------------------------------
    // LastPass (injects sibling div wrappers/roots with stable data-* attributes)
    // Evidence: div[data-lastpass-icon-root] and div[data-lastpass-root].  https://stackoverflow.com/questions/50145270/hide-last-pass-icon-in-input-fields
    // ---------------------------------------------------------------------------
    'div[data-lastpass-icon-root]',
    'div[data-lastpass-root]',
    // Also seen in the wild (in-field UI variant)
    'div[data-lastpass-infield]',

    // ---------------------------------------------------------------------------
    // Bitwarden
    // - Adds data-bitwarden-watching on forms (easy to detect).
    // - Overlay UI is rendered in closed Shadow DOM with a randomized host element
    //   name (so you can’t rely on a stable tag selector).
    // Evidence: data-bitwarden-watching appears on <form>.  https://stackoverflow.com/questions/64363939/phpmyadmin-login-form-hidden
    // ---------------------------------------------------------------------------
    'form[data-bitwarden-watching]',
    '[data-bitwarden-watching]',

    // ---------------------------------------------------------------------------
    // Dashlane
    // Evidence: Dashlane injects attributes like data-dashlanecreated / data-dashlane-rid
    // (seen in real page HTML snippets + tooling).  https://socket.dev/npm/package/input-otp/diff/1.3.0
    // Evidence: field opt-out attributes documented publicly. https://stackoverflow.com/questions/59939931/stop-dashlane-auto-fill-on-specific-input-fields
    // ---------------------------------------------------------------------------
    '[data-dashlanecreated]',
    '[data-dashlane-rid]',
    // Dashlane also toggles a head marker around Shadow DOM handling in some cases
    'head[data-dashlane-shadowhost]',

    // Field-level opt-outs (if you can add them)
    "[data-dashlane-disabled-on-field='true']",
    "[data-form-type='other']",

    // ---------------------------------------------------------------------------
    // Proton Pass (injects a root custom element; also uses randomized root variants)
    // Evidence: protonpass-root and protonpass-root-* patterns shown in research write-up.  https://marektoth.com/blog/dom-based-extension-clickjacking/
    // ---------------------------------------------------------------------------
    'protonpass-root',
    "[is^='protonpass-root-']",
    "[class^='protonpass-root-']",
    "[id^='protonpass-root-']",
];

/**
 * Hook to ensure that password manager interactions are properly captured within React Aria Modals.
 * React Area Modals inject `inert` attributes to prevent background interaction, which can interfere
 * with password manager UIs. This hook uses a list of known password manager selectors to ensure
 * that interactions with their UI elements are not blocked.
 */
export const useEnsurePasswordManagersInteraction = () => {
    useEnsureModalInteraction({ selector: PASSWORD_MANAGER_SELECTORS });
};
